import { CopyOutlined } from "@ant-design/icons";
import { Button, Drawer, Flex, Input, message } from "antd";
import { NodeFormData } from "components/CC/utils/nodeDefaults.ts";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useCallback, useEffect, useState } from "react";

interface NodeBlueprintDrawerProps {
    open: boolean;
    onClose: () => void;
    nodeType: string;
    formData: NodeFormData | undefined;
    onApply: (formData: NodeFormData) => void;
}

export const NodeBlueprintDrawer = ({ open, onClose, nodeType, formData, onApply }: NodeBlueprintDrawerProps) => {
    const { t } = usePaldockTranslation("components.connection-creator");
    const { t: tC } = usePaldockTranslation("common");
    const [content, setContent] = useState("");
    const [isValid, setIsValid] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Sync internal state with external value when drawer opens
    useEffect(() => {
        if (open) {
            const exportData = {
                nodeType,
                formData: formData || {},
            };
            setContent(JSON.stringify(exportData, null, 2));
            setIsValid(true);
            setError(null);
        }
    }, [open, nodeType, formData]);

    // Validate JSON as user types
    const handleContentChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newContent = e.target.value;
            setContent(newContent);

            try {
                const parsed = JSON.parse(newContent);

                // Validate structure
                if (!parsed.nodeType || typeof parsed.nodeType !== "string") {
                    setIsValid(false);
                    setError(t("blueprint.error_invalid_node_type"));
                    return;
                }

                // Check node type matches
                if (parsed.nodeType !== nodeType) {
                    setIsValid(false);
                    setError(t("blueprint.error_node_type_mismatch", { expected: nodeType, actual: parsed.nodeType }));
                    return;
                }

                if (!parsed.formData || typeof parsed.formData !== "object") {
                    setIsValid(false);
                    setError(t("blueprint.error_invalid_form_data"));
                    return;
                }

                setIsValid(true);
                setError(null);
            } catch {
                setIsValid(false);
                setError(t("blueprint.error_invalid_json"));
            }
        },
        [t, nodeType]
    );

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(content);
        message.success(t("blueprint.copied"));
    }, [content, t]);

    const handleApply = useCallback(() => {
        try {
            const parsed = JSON.parse(content);
            onApply(parsed.formData);
            onClose();
            message.success(t("blueprint.applied"));
        } catch {
            message.error(t("blueprint.error_invalid_json"));
        }
    }, [content, onApply, onClose, t]);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={<DrawerTitle mainTitle={t("blueprint.node_title")} />}
            width={600}
            extra={
                <Flex gap="small">
                    <Button size="large" icon={<CopyOutlined />} onClick={handleCopy}>
                        {tC("copy")}
                    </Button>
                    <Button size="large" onClick={onClose}>
                        {tC("cancel")}
                    </Button>
                    <Button size="large" type="primary" onClick={handleApply} disabled={!isValid}>
                        {tC("apply")}
                    </Button>
                </Flex>
            }
        >
            <DrawerContent>
                <p className="text-gray-500 mb-4">{t("blueprint.node_description")}</p>
                <Input.TextArea
                    value={content}
                    onChange={handleContentChange}
                    rows={20}
                    status={!isValid ? "error" : undefined}
                    style={{ fontFamily: "monospace", fontSize: 12 }}
                />
                {error && <p className="text-red-500 mt-2">{error}</p>}
            </DrawerContent>
        </Drawer>
    );
};
